import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Select } from 'components/Shared';
import type { SelectOption } from 'contracts/Common';
import {
  DeleteCompanyWarehouseAccessActions as DeleteActions,
  CreateCompanyWarehouseAccessActions as CreateActions,
} from 'store/ducks/companyWarehouseAccesses';
import { useValidation } from 'hooks';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import * as Yup from 'yup';
import * as S from './styles';
import { Formatter } from 'utils';
import { CompanyWarehouseAccess } from 'contracts/CompanyWarehouseAccesses';

export interface Ref {
  resetForm: () => void;
}

interface Props {
  onUpdate?: () => void;
  companyMemberId: string;
}

interface IWarehousesManager
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

interface ItemProps {
  warehouse: CompanyWarehouseAccess['warehouse'];
  id: number;
}

const WarehousesManager: IWarehousesManager = forwardRef<Ref, Props>(
  ({ companyMemberId, onUpdate }, ref) => {
    const { data: companyWarehouseAccesses, loading: loadingAccesses } =
      useSelector((state: RootState) => state.paginateCompanyWarehouseAccesses);

    const dispatch: AppDispatch = useDispatch();
    const formRef = useRef<FormHandles>(null);

    const { handleFormErrors, handleApiErrors } = useValidation();

    /**
     * this component will not fetch warehouses here,
     * we lifted it to a upper level (RulesManager component)
     * to avoid dispatching multiple times
     * */
    const { loading: loadingWarehouses, data: warehouses } = useSelector(
      (state: RootState) => state.listWarehouses
    );

    const { loading: creating, validationErrors } = useSelector(
      (state: RootState) => state.createCompanyWarehouseAccess
    );

    const { id: deletingId } = useSelector(
      (state: RootState) => state.deleteCompanyWarehouseAccess
    );

    /**
     * we will also filter the list of warehouses to only show
     * the ones that are not already bound to the dock
     * */

    const warehouseOptions: SelectOption[] | undefined = useMemo(() => {
      if (!warehouses?.length) return undefined;

      return warehouses
        .filter(({ id }) =>
          companyWarehouseAccesses.length
            ? !companyWarehouseAccesses
                .map(({ warehouseId }) => warehouseId)
                .includes(id)
            : true
        )
        .map((warehouse) => ({
          value: warehouse.id,
          label: `${warehouse.tradeName} - ${Formatter.document(
            warehouse.document,
            warehouse.documentType
          )}`,
        }));
    }, [warehouses, companyWarehouseAccesses]);

    const onDelete = useCallback(
      (id?: number) => dispatch(DeleteActions.request(id, onUpdate)),
      [dispatch, onUpdate]
    );

    const onSuccess = useCallback((): void => {
      onUpdate && onUpdate();
      formRef.current?.reset();
    }, [onUpdate]);

    const onSubmit = useCallback(
      async (data: any): Promise<void> => {
        try {
          formRef.current?.setErrors({});
          const schema = Yup.object().shape({
            warehouseId: Yup.number()
              .typeError('Selecione o armazém')
              .required('Selecione o armazém'),
          });
          const validData = await schema.validate(data, {
            abortEarly: false,
          });
          dispatch(
            CreateActions.request({ ...validData, companyMemberId }, onSuccess)
          );
        } catch (error) {
          handleFormErrors(error, formRef);
        }
      },
      [dispatch, handleFormErrors, onSuccess, companyMemberId]
    );

    const Item = useCallback(
      ({ warehouse, id }: ItemProps): JSX.Element => {
        const {
          addressCity,
          addressState,
          document,
          id: warehouseId,
          tradeName,
        } = warehouse;

        return (
          <S.ListItem>
            <S.Column>{warehouseId}</S.Column>
            <S.Column>{tradeName}</S.Column>
            <S.Column>{Formatter.cnpj(document)}</S.Column>
            <S.Column>{addressCity}</S.Column>
            <S.Column>{addressState}</S.Column>
            <S.ActionsColumn>
              <S.ActionButton
                disabled={deletingId === id}
                onClick={() => onDelete(id)}
                mood="danger"
              >
                {deletingId === id ? (
                  <S.ActivityIndicator />
                ) : (
                  <S.TrashIcon />
                )}
              </S.ActionButton>
            </S.ActionsColumn>
          </S.ListItem>
        );
      },
      [deletingId, onDelete]
    );

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          formRef.current?.reset();
          formRef.current?.setErrors({});
        },
      }),
      []
    );

    useEffect(() => {
      handleApiErrors(validationErrors, formRef);
    }, [handleApiErrors, validationErrors]);

    return (
      <S.Container>
        <Form ref={formRef} onSubmit={onSubmit}>
          <S.FormRow>
            <Select
              name="warehouseId"
              label="Armazém"
              options={warehouseOptions}
              isLoading={loadingWarehouses || loadingAccesses}
              menuPortalTarget={document.body}
              menuPlacement="top"
            />
            <S.Button type="submit">
              {creating ? <S.ActivityIndicator /> : 'Adicionar'}
            </S.Button>
          </S.FormRow>
        </Form>
        {!companyWarehouseAccesses?.length ? (
          <S.EmptyListPlaceholder>
            Esse usuário ainda não possui restrições para armazéns
          </S.EmptyListPlaceholder>
        ) : (
          <>
            <S.ListHeader>
              <div>ID</div>
              <div>NOME</div>
              <div>CNPJ</div>
              <div>CIDADE</div>
              <div>ESTADO</div>
            </S.ListHeader>
            {companyWarehouseAccesses?.map((props) => (
              <Item {...props} key={props.id} />
            ))}
          </>
        )}
      </S.Container>
    );
  }
);

export default WarehousesManager;
